import {
  Input,
  Select,
  FormCard,
  FormItem,
  DatePicker,
  TextArea
} from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import {
  MARITAL_STATUSES,
  NATIONALITIES,
  LANGUAGES,
  religionsOptions,
  newEthnicities,
  pregnancyStatusOptions
} from 'common/constants'
import { Text } from '@lmc/ui-kit/src/general'
import { dateFormat, getSelectOptions } from '../../../utils/utils'
import styled from 'styled-components'
import { SupDatePicker } from '../../../styledComponents'
import { Gender, ResidentStatus } from '@lmc/ts-common'
import { Tooltip } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import dayjs from 'dayjs'
import { ResidentFetchItemResponse } from '@lmc/validations'

type DemographicFormCardProps = {
  residentAge: number | undefined
  isPro: boolean
  resident?: ResidentFetchItemResponse
}

export const DemographicFormCard = ({
  residentAge,
  isPro,
  resident
}: DemographicFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const flags = useFeatureFlags({
    flagNames: ['prsb_resident_gender']
  })?.data
  const isPrsbGenderEnabled = flags?.prsb_resident_gender?.enabled

  const isDeceased = resident?.status === ResidentStatus.deceased
  const deathDateValue = resident?.deathDate
    ? dayjs(resident.deathDate)
    : undefined
  const deathDetails = resident?.deathDetails || ''

  const getGenderOptions = () => {
    const allowedGendersArray = [Gender.male, Gender.female]

    if (isPrsbGenderEnabled) {
      allowedGendersArray.push(Gender.unknown, Gender.unspecified, Gender.other)
    }

    const genderOptions = allowedGendersArray.map((gender) => ({
      value: gender,
      label: t(supConstants[gender])
    }))

    return genderOptions
  }

  const sortedReligions = religionsOptions.sort((a, b) =>
    a.label > b.label ? 1 : -1
  )

  return (
    <FormCard title={t(supConstants.DEMOGRAPHIC)}>
      <FormItem
        label={<Text>{t(supConstants.GENDER)}</Text>}
        name="gender"
        rules={[
          {
            required: true
          }
        ]}
      >
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          options={getGenderOptions()}
        />
      </FormItem>
      <AgeContainer>
        <FormItem
          label={<Text>{t(supConstants.DATE_OF_BIRTH)}</Text>}
          name="dateOfBirth"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${t(
                supConstants.DATE_OF_BIRTH
              )}`
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        {residentAge && (
          <BaseText>
            {t(supConstants.AGE)} {residentAge}
          </BaseText>
        )}
      </AgeContainer>
      {isDeceased && (deathDateValue || deathDetails) && (
        <>
          {deathDateValue && (
            <FormItem
              label={<Text>{t(supConstants.DATE_OF_DEATH)}</Text>}
              data-testid="death-details-date"
            >
              <DatePicker value={deathDateValue} format={dateFormat} disabled />
            </FormItem>
          )}
          {deathDetails && (
            <FormItem
              label={<Text>{t(supConstants.DETAILS_OF_DEATH)}</Text>}
              data-testid="death-details-text"
            >
              <TextArea value={deathDetails} disabled rows={2} />
            </FormItem>
          )}
          <HelpText>{t(supConstants.DEATH_DETAILS_EDIT_HINT)}</HelpText>
        </>
      )}
      {isPro && (
        <>
          <FormItem
            label={<Text>{t(supConstants.MARITAL_STATUS)}</Text>}
            name="maritalStatus"
          >
            <Select options={maritalStatusOptions} />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.BIRTHPLACE)}</Text>}
            name="birthplace"
          >
            <Input maxLength={50} />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.NATIONALITY)}</Text>}
            name="nationalities"
          >
            <Select options={getSelectOptions(NATIONALITIES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.LANGUAGES_SPOKEN)}</Text>}
            name="languagesSpoken"
          >
            <Select options={getSelectOptions(LANGUAGES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.ETHNICITY)}</Text>}
            name="ethnicity"
          >
            <Select
              optionRender={(option) => <Text>{option.label}</Text>}
              options={newEthnicities}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.RELIGION)}</Text>}
            name="religion"
          >
            <Select
              showSearch
              options={sortedReligions}
              optionRender={(option) => (
                <Tooltip placement="top" title={option.label}>
                  <Text>{option.label}</Text>
                </Tooltip>
              )}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.PREGNANCY_STATUS)}</Text>}
            name="pregnancyStatus"
          >
            <Select
              options={pregnancyStatusOptions}
              optionRender={(option) => <Text>{option.label}</Text>}
              allowClear
            />
          </FormItem>
        </>
      )}
    </FormCard>
  )
}

const maritalStatusOptions = MARITAL_STATUSES.map((maritalStatus) => ({
  value: maritalStatus.value,
  label: <Text>{maritalStatus.value}</Text>
}))

const AgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const BaseText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const HelpText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  font-size: 13px;
  font-style: italic;
  margin-bottom: 16px;
`
